// ./src/components/features/SignupItems/SignupItemsFilterBar/SignupItemsFilterBar.tsx
//
// Filter controls for the event SignupItems list: by status (active / disabled)
// and by lodging category (from the shared "signup" vocabulary). Fully
// controlled — the page owns the value and maps it to an SignupItemFilter.
// Name/address search lives in the page header, not here.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon, CategorySelect } from "@/components/generic";

export type SignupItemStatusFilter = "active" | "disabled";

export interface SignupItemsFilterValue {
  status: SignupItemStatusFilter | null;
  categoryId: string | null;
}

export interface SignupItemsFilterBarProps {
  value: SignupItemsFilterValue;
  onChange: (value: SignupItemsFilterValue) => void;
  disabled?: boolean;
  showClear?: boolean;
}

export const EMPTY_SIGNUP_ITEMS_FILTER: SignupItemsFilterValue = {
  status: null,
  categoryId: null,
};

export const signupItemsFilterActiveCount = (value: SignupItemsFilterValue): number =>
  [value.status, value.categoryId].filter(Boolean).length;

const isFilterActive = (value: SignupItemsFilterValue): boolean =>
  signupItemsFilterActiveCount(value) > 0;

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 260px", minWidth: 240 };
const CATEGORY_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";

const SignupItemsFilterBarComponent: React.FC<SignupItemsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const statusOptions = [
    { value: ANY, label: t("events:signupItems.filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("events:signupItems.filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("events:signupItems.filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("events:signupItems.filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as SignupItemStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={CATEGORY_STYLE}>
        <CategorySelect
          group="signup"
          exactGroup
          label={t("events:signupItems.filters.category.label", { defaultValue: "Category" })}
          placeholder={t("events:signupItems.filters.category.placeholder", { defaultValue: "All categories" })}
          value={value.categoryId}
          onChange={(categoryId) => onChange({ ...value, categoryId })}
          disabled={disabled}
          clearable
          size="md"
        />
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_SIGNUP_ITEMS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const SignupItemsFilterBar = React.memo(SignupItemsFilterBarComponent);
